import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PlayCircle, FileText, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const PAGE_SIZE = 15;

const STATUS_LABEL = {
  pending: "Pendente",
  in_progress: "Em andamento",
  completed: "Concluída",
};

const STATUS_TONE = {
  pending: "bg-stone-100 text-stone-700",
  in_progress: "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

export default function Executions() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [fStatus, setFStatus] = useState("all");
  const [fTemplate, setFTemplate] = useState("all");
  const [fPeriod, setFPeriod] = useState("all");
  const navigate = useNavigate();
  const { user } = useAuth();
  const canExport = ["super_admin", "company_admin", "manager", "supervisor"].includes(user?.role);

  const exportPdf = async () => {
    try {
      const params = new URLSearchParams();
      if (fStatus !== "all") params.set("status", fStatus);
      if (fTemplate !== "all") params.set("template_name", fTemplate);
      if (fPeriod !== "all") params.set("period", fPeriod);
      const res = await api.get(`/checklists/executions/export.pdf?${params.toString()}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "execucoes.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Relatório de execuções exportado");
    } catch {
      toast.error("Falha ao exportar PDF");
    }
  };

  useEffect(() => {
    api
      .get("/checklists/executions?limit=500")
      .then(({ data }) => setItems(data))
      .catch((err) => console.error("Failed to load executions", err));
  }, []);

  useEffect(() => {
    setPage(1);
  }, [fStatus, fTemplate, fPeriod]);

  const templateNames = useMemo(
    () => [...new Set(items.map((e) => e.template_name).filter(Boolean))].sort(),
    [items]
  );

  const filtered = useMemo(() => {
    const now = new Date();
    const dayMs = 24 * 60 * 60 * 1000;
    const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return items.filter((e) => {
      if (fStatus !== "all" && e.status !== fStatus) return false;
      if (fTemplate !== "all" && e.template_name !== fTemplate) return false;
      if (fPeriod !== "all") {
        const created = new Date(e.created_at);
        if (fPeriod === "today" && created < startToday) return false;
        if (fPeriod === "7d" && now - created > 7 * dayMs) return false;
        if (fPeriod === "30d" && now - created > 30 * dayMs) return false;
      }
      return true;
    });
  }, [items, fStatus, fTemplate, fPeriod]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );

  return (
    <Layout title="Execuções" subtitle="Histórico de checklists realizados">
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="w-44 bg-white" data-testid="executions-filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="pending">Pendente</SelectItem>
            <SelectItem value="in_progress">Em andamento</SelectItem>
            <SelectItem value="completed">Concluída</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fTemplate} onValueChange={setFTemplate}>
          <SelectTrigger className="w-60 bg-white" data-testid="executions-filter-template">
            <SelectValue placeholder="Checklist" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os checklists</SelectItem>
            {templateNames.map((n) => (
              <SelectItem key={n} value={n}>
                {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={fPeriod} onValueChange={setFPeriod}>
          <SelectTrigger className="w-44 bg-white" data-testid="executions-filter-period">
            <SelectValue placeholder="Período" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todo o período</SelectItem>
            <SelectItem value="today">Hoje</SelectItem>
            <SelectItem value="7d">Últimos 7 dias</SelectItem>
            <SelectItem value="30d">Últimos 30 dias</SelectItem>
          </SelectContent>
        </Select>
        {(fStatus !== "all" || fTemplate !== "all" || fPeriod !== "all") && (
          <Button
            variant="ghost"
            size="sm"
            className="text-stone-500"
            onClick={() => {
              setFStatus("all");
              setFTemplate("all");
              setFPeriod("all");
            }}
            data-testid="executions-filter-clear"
          >
            Limpar filtros
          </Button>
        )}
        {canExport && (
          <Button
            variant="outline"
            className="border-stone-300 bg-white ml-auto"
            onClick={exportPdf}
            data-testid="executions-export-pdf"
          >
            <Download className="h-4 w-4 mr-2" /> Exportar PDF
          </Button>
        )}
      </div>
      <Card data-testid="executions-page" className="rounded-xl border-stone-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="font-body">Checklist</TableHead>
              <TableHead className="font-body">Executor</TableHead>
              <TableHead className="font-body">Iniciado em</TableHead>
              <TableHead className="font-body">Status</TableHead>
              <TableHead className="font-body">Conformidade</TableHead>
              <TableHead className="font-body text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-stone-500 py-10">
                  <FileText className="h-6 w-6 mx-auto mb-2 text-stone-400" />
                  {items.length === 0
                    ? "Nenhuma execução ainda"
                    : "Nenhuma execução encontrada com os filtros aplicados"}
                </TableCell>
              </TableRow>
            )}
            {pageItems.map((e) => (
              <TableRow key={e.id} data-testid={`execution-row-${e.id}`}>
                <TableCell className="font-display font-semibold">{e.template_name || "—"}</TableCell>
                <TableCell className="font-body text-sm">{e.executor_name || "—"}</TableCell>
                <TableCell className="font-body text-sm">{formatDateTime(e.started_at || e.created_at)}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={STATUS_TONE[e.status]}>
                    {STATUS_LABEL[e.status] || e.status}
                  </Badge>
                  {e.geo_out_of_range === true && (
                    <Badge className="ml-1 bg-rose-600 text-white border-none text-[10px]" data-testid={`execution-geo-out-${e.id}`}>
                      Fora do local
                    </Badge>
                  )}
                  {e.geo_out_of_range === false && (
                    <Badge className="ml-1 bg-emerald-700 text-white border-none text-[10px]" data-testid={`execution-geo-ok-${e.id}`}>
                      No local
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="font-body">
                  {e.status === "completed" ? (
                    <span className={`font-display font-bold ${e.compliance_rate >= 90 ? "text-emerald-700" : e.compliance_rate >= 70 ? "text-amber-700" : "text-rose-700"}`}>
                      {e.compliance_rate?.toFixed(0)}%
                    </span>
                  ) : (
                    <span className="text-stone-400">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/executions/${e.id}`)}
                    data-testid={`execution-open-${e.id}`}
                  >
                    <PlayCircle className="h-3.5 w-3.5 mr-1" />
                    {e.status === "completed" ? "Ver" : "Continuar"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-stone-200 bg-stone-50/50">
            <span className="text-xs text-stone-500 font-body" data-testid="executions-page-info">
              Página {page} de {totalPages} · {filtered.length} execuções
            </span>
            <div className="flex gap-1.5">
              <Button
                variant="outline"
                size="sm"
                className="bg-white"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                data-testid="executions-prev-page"
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="bg-white"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                data-testid="executions-next-page"
              >
                Próxima <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </Layout>
  );
}
